// Policy — the Govern pillar as an enforcement control surface.
//
// Rendered on the REAL BlueRock design system (@bluerock/corenet + antd),
// matching the agate cost-dashboard idiom: antd Card/Statistic KPI strip,
// Flex-stacked sections, and corenet's PolicyModeControl (the canonical
// Off/Alert/Block segmented control) + ChipEditor for policy-form fields.
//
// Structure (per David's direction 2026-07-08):
//   1. KPI strip      — active policy, mode, counts. No alert-feed data.
//   2. Policies list  — ALL policies; top 3 visible, expander for the rest.
//   3. Policy detail  — the selected policy rendered as clearly separated
//                       sections (MCP / Processes / Files / Network /
//                       Content / Kernel). Off sections still render, greyed.
//
// The JSON editor remains the advanced escape hatch behind Edit.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Flex,
  Input,
  InputNumber,
  Modal,
  Popover,
  Radio,
  Row,
  Segmented,
  Skeleton,
  Statistic,
  Table,
  Tag,
  Typography,
  theme,
} from "antd";
import {
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  Copy,
  Plus,
  X,
  Sparkles,
  Info,
  Globe,
  FileLock2,
  Terminal,
  Activity,
  Plug,
  Filter,
  Bot,
  Pin,
  DollarSign,
  Server,
  ShieldHalf,
  type LucideIcon,
} from "lucide-react";
import { PolicyModeControl, ChipEditor, type PolicyMode } from "@bluerock/corenet/policy";
import { emptyContent } from "@/components/PolicyEditor";
import { useFetch } from "@/lib/api/useFetch";
import { useApi, newIdempotencyKey, describeError } from "@/lib/api/client";
import { AGENT_SENSORS, type AgentRemediate, type ExtendedContent, type ExtendedMcpServer } from "@/lib/policyExt";
import HostSensorsSection from "@/components/HostSensorsSection";
import AgentContainmentPanel from "@/components/AgentContainmentPanel";
import McpToolFilterEditor from "@/components/McpToolFilterEditor";
import { SENSOR_INFO, type SensorArea } from "@/lib/sensorInfo";
import { TOKENS } from "@/lib/chartColors";
import PageHeader from "@/components/PageHeader";
import { HintIcon } from "@/components/agate";
import type { components } from "@/lib/api/gen/schema";

const { Text } = Typography;

type Policy = components["schemas"]["Policy"];
type PolicyContent = components["schemas"]["PolicyContent"];

// Proposal — a staged rule arriving from Discovery via ?propose= (the
// finding→rule closed loop). The evidence string travels with it so the
// operator sees WHY the rule is being staged before committing.
type Proposal = { kind: "block-server" | "block-model" | "spend-cap"; arg: string; evidence?: string };

function parseProposal(params: URLSearchParams): Proposal | null {
  const raw = params.get("propose");
  if (!raw) return null;
  const [kind, ...rest] = raw.split(":");
  const arg = rest.join(":");
  if (kind === "block-server" || kind === "block-model") {
    if (!arg) return null;
    return { kind, arg, evidence: params.get("evidence") ?? undefined };
  }
  if (kind === "spend-cap") return { kind, arg: "", evidence: params.get("evidence") ?? undefined };
  return null;
}

// enforcementMode derives whether a policy actively BLOCKS (Enforce) or only
// alerts/allows (Observe) from its content — any MCP deny or egress restriction
// means it blocks. Drives the mode indicator so operators see intent at a glance.
function enforcementMode(c?: PolicyContent): "Enforce" | "Observe" {
  const mcp = c?.mcp;
  const mcpDenies =
    mcp?.default_stdio_policy === "deny" ||
    mcp?.default_sse_policy === "deny" ||
    mcp?.default_http_policy === "deny";
  if (mcpDenies && mcp?.remediate !== false) return "Enforce";
  if (c?.brace?.network?.enable) return "Enforce";
  return "Observe";
}

type FamilyState = "enforcing" | "detect" | "off";

function toggleState(t?: { enable?: boolean; remediate?: boolean }): FamilyState {
  if (!t?.enable) return "off";
  return t.remediate ? "enforcing" : "detect";
}

// BlueRock vocabulary (testplan): observe mode logs a violation (WARN);
// remediation blocks it (ERROR). So the family states read Blocking /
// Observe / Off — not "enforce/detect".
function stateLabel(s: FamilyState): { label: string; color: string } {
  if (s === "enforcing") return { label: "Blocking", color: TOKENS.governance };
  if (s === "detect") return { label: "Observe", color: TOKENS.warning };
  return { label: "Off", color: TOKENS.textMuted };
}

// Known model catalog for the Model-pinning picker — grouped by provider so
// operators pick from a menu instead of typing raw ids (custom ids still allowed).
const MODEL_CATALOG: { family: string; models: string[] }[] = [
  { family: "Anthropic — Claude", models: ["claude-opus-5", "claude-sonnet-5", "claude-fable-5", "claude-haiku-4-5", "claude-opus-4-8", "claude-opus-4-7", "claude-opus-4-6", "claude-sonnet-4-6", "claude-opus-4-5", "claude-sonnet-4-5"] },
  { family: "OpenAI", models: ["gpt-5.6-sol", "gpt-5.6-terra", "gpt-5.6-luna", "gpt-5.6-cyber", "gpt-5.4", "gpt-5.4-mini", "gpt-5.4-nano", "gpt-5.1", "gpt-5", "gpt-4.1", "gpt-4o"] },
  { family: "Google — Gemini", models: ["gemini-3.7-flash", "gemini-3.6-flash", "gemini-3.5-flash", "gemini-3.5-flash-lite", "gemini-3.1-pro", "gemini-3.1-flash-lite", "gemini-2.5-pro", "gemini-2.5-flash", "gemini-2.5-flash-lite"] },
];
const CATALOG_MODELS = new Set(MODEL_CATALOG.flatMap((f) => f.models));

// Per-agent tool catalogs — the governable built-in tools each coding agent
// exposes, matched by exact name at that agent's pre-tool hook (Claude
// PreToolUse / Gemini BeforeTool / Cursor preToolUse / Codex PreToolUse).
// Verbatim from the trex enums (ClaudeTool/GeminiTool/CodexTool/CursorTool in
// acoustic/trex/trex/models/customerpolicymodel.py), grouped for the picker.
// The `MCP` sentinel (denies every mcp__* call) is handled separately, not
// listed as a tool. Keyed by AGENT_SENSORS content key.
const AGENT_TOOL_CATALOG: Record<string, { group: string; tools: string[] }[]> = {
  claude_sensor: [
    { group: "Shell & execution", tools: ["Bash", "BashOutput", "KillShell"] },
    { group: "Filesystem", tools: ["Read", "Write", "Edit", "NotebookEdit", "Glob", "Grep"] },
    { group: "Web", tools: ["WebFetch", "WebSearch"] },
    { group: "Tasks & agents", tools: ["Task", "TodoWrite", "TaskCreate", "TaskUpdate", "TaskStop"] },
    { group: "Meta & dispatch", tools: ["ToolSearch", "ExitPlanMode", "AskUserQuestion", "Skill", "SlashCommand"] },
  ],
  gemini_sensor: [
    { group: "Shell & execution", tools: ["run_shell_command"] },
    { group: "Filesystem", tools: ["read_file", "read_many_files", "write_file", "replace", "glob", "grep_search", "search_file_content", "list_directory"] },
    { group: "Web", tools: ["web_fetch", "google_web_search"] },
  ],
  codex_sensor: [
    { group: "Shell & execution", tools: ["Bash", "shell", "unified_exec"] },
    { group: "Filesystem", tools: ["apply_patch", "read_file", "list_dir", "glob_file_search", "file_search"] },
    { group: "Web", tools: ["web_search"] },
    { group: "Meta & dispatch", tools: ["view_image", "update_plan"] },
  ],
  cursor_sensor: [
    { group: "Shell & execution", tools: ["Shell"] },
    { group: "Filesystem", tools: ["Read", "Write", "Edit", "Grep", "Delete"] },
    { group: "Tasks & agents", tools: ["Task"] },
  ],
};
const agentToolCount = (key: string) => (AGENT_TOOL_CATALOG[key] ?? []).reduce((n, g) => n + g.tools.length, 0);

// ModelCatalogPicker — pick allowed/blocked models from the provider catalog
// (green when allow-listed, red when block-listed), with a per-family select-all
// and a custom-id escape hatch. Matches the Agate model-catalog mockup.
function ModelCatalogPicker({
  value,
  mode,
  onChange,
}: {
  value: string[];
  mode: "allowlist" | "blocklist";
  onChange: (v: string[]) => void;
}) {
  const { token } = theme.useToken();
  const sel = new Set(value);
  const allow = mode === "allowlist";
  const onColor = allow ? token.colorSuccess : token.colorError;
  const onBg = allow ? token.colorSuccessBg : token.colorErrorBg;
  const toggle = (m: string) => {
    const n = new Set(sel);
    if (n.has(m)) n.delete(m);
    else n.add(m);
    onChange([...n]);
  };
  const setFamily = (models: string[], on: boolean) => {
    const n = new Set(sel);
    models.forEach((m) => (on ? n.add(m) : n.delete(m)));
    onChange([...n]);
  };
  const groupLabel = { fontSize: 11, textTransform: "uppercase" as const, letterSpacing: 0.5, color: token.colorTextTertiary, fontWeight: 600 };
  const custom = value.filter((v) => !CATALOG_MODELS.has(v));
  return (
    <Flex vertical gap={14}>
      {MODEL_CATALOG.map((f) => {
        const onCount = f.models.filter((m) => sel.has(m)).length;
        const allOn = onCount === f.models.length;
        return (
          <div key={f.family}>
            <Flex align="center" justify="space-between" style={{ marginBottom: 6 }}>
              <Text style={groupLabel}>
                {f.family}
                {onCount > 0 ? ` · ${onCount} ${allow ? "allowed" : "blocked"}` : ""}
              </Text>
              <Button size="small" type="link" style={{ fontSize: 12, padding: 0, height: "auto" }} onClick={() => setFamily(f.models, !allOn)}>
                {allOn ? "Clear" : "Select all"}
              </Button>
            </Flex>
            <Flex wrap="wrap" gap={6}>
              {f.models.map((m) => {
                const on = sel.has(m);
                return (
                  <span
                    key={m}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggle(m)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggle(m))}
                    style={{
                      cursor: "pointer",
                      fontFamily: token.fontFamilyCode,
                      fontSize: 12,
                      padding: "3px 9px",
                      borderRadius: 8,
                      border: `1px solid ${on ? onColor : token.colorBorder}`,
                      background: on ? onBg : token.colorBgContainer,
                      color: on ? onColor : token.colorText,
                      userSelect: "none",
                    }}
                  >
                    {m}
                  </span>
                );
              })}
            </Flex>
          </div>
        );
      })}
      <div>
        <Text style={{ ...groupLabel, display: "block", marginBottom: 4 }}>Custom model IDs</Text>
        <ChipEditor value={custom} placeholder="org/model or exact id" onChange={(c) => onChange([...value.filter((v) => CATALOG_MODELS.has(v)), ...c])} />
      </div>
    </Flex>
  );
}

// AgentToolPicker — click the specific tools this agent may not call. Selected
// (governed) tools go red and land in the tool_guard `denied` list; the posture
// above (Off/Detect/Enforce) governs the whole block. The MCP sentinel is a
// separate toggle (denies every mcp__* call). Mirrors the Claude-hooks mockup.
function AgentToolPicker({
  groups,
  denied,
  onChange,
}: {
  groups: { group: string; tools: string[] }[];
  denied: string[];
  onChange: (v: string[]) => void;
}) {
  const { token } = theme.useToken();
  const sel = new Set(denied);
  const toggle = (t: string) => {
    const n = new Set(sel);
    if (n.has(t)) n.delete(t);
    else n.add(t);
    onChange([...n]);
  };
  const groupLabel = { fontSize: 11, textTransform: "uppercase" as const, letterSpacing: 0.5, color: token.colorTextTertiary, fontWeight: 600 };
  const mcpOn = sel.has("MCP");
  return (
    <Flex vertical gap={12}>
      {groups.map((g) => (
        <div key={g.group}>
          <Text style={{ ...groupLabel, display: "block", marginBottom: 6 }}>{g.group}</Text>
          <Flex wrap="wrap" gap={6}>
            {g.tools.map((t) => {
              const on = sel.has(t);
              return (
                <span
                  key={t}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(t)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggle(t))}
                  style={{
                    cursor: "pointer",
                    fontFamily: token.fontFamilyCode,
                    fontSize: 12,
                    padding: "3px 9px",
                    borderRadius: 8,
                    border: `1px solid ${on ? token.colorError : token.colorBorder}`,
                    background: on ? token.colorErrorBg : token.colorBgContainer,
                    color: on ? token.colorError : token.colorText,
                    userSelect: "none",
                  }}
                >
                  {t}
                </span>
              );
            })}
          </Flex>
        </div>
      ))}
      {/* MCP sentinel — one toggle denies every mcp__* call, gated on the agent's
          MCP hook. Per-server / per-tool control lives on the MCP filtering surface. */}
      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          cursor: "pointer",
          border: `1px solid ${mcpOn ? token.colorError : token.colorBorderSecondary}`,
          background: mcpOn ? token.colorErrorBg : token.colorFillQuaternary,
          borderRadius: 8,
          padding: "8px 10px",
        }}
      >
        <Checkbox checked={mcpOn} onChange={() => toggle("MCP")} />
        <span>
          <Text strong style={{ fontSize: 12 }}>Block all MCP tools</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            The <Text code style={{ fontSize: 11 }}>MCP</Text> sentinel blocks every <Text code style={{ fontSize: 11 }}>mcp__*</Text> call. For per-server / per-tool control, use the MCP filtering surface.
          </Text>
        </span>
      </label>
    </Flex>
  );
}

// Posture as colored text + mono glyph, not a pill:
// enforcing ● danger / detect ◐ warning / off ○ muted.
function ModeTag({ state }: { state: FamilyState }) {
  const m =
    state === "enforcing"
      ? { label: "Blocking", cls: "text-danger", glyph: "●" }
      : state === "detect"
        ? { label: "Observe", cls: "text-warning", glyph: "◐" }
        : { label: "Off", cls: "text-text-muted", glyph: "○" };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${m.cls}`}>
      <span className="font-mono text-[10px] leading-none">{m.glyph}</span>
      {m.label}
    </span>
  );
}

// PolicyModeControl (corenet) speaks off/alert/block. Our internal family
// states speak off/detect/enforce. Bridge the two vocabularies.
const toMode = (s: FamilyState | "off" | "detect" | "enforce"): PolicyMode =>
  s === "enforcing" || s === "enforce" ? "block" : s === "detect" ? "alert" : "off";
const fromMode = (m: PolicyMode): "off" | "detect" | "enforce" =>
  m === "block" ? "enforce" : m === "alert" ? "detect" : "off";

// Model-governance and spend-guardrail states (extension domains — see
// lib/policyExt.ts; they graduate into openapi.yaml with the Go backend).
function modelsState(c?: PolicyContent): FamilyState {
  const m = (c as ExtendedContent | undefined)?.models;
  if (!m?.models?.length) return "off";
  return m.on_violation === "alert" ? "detect" : "enforcing";
}
function spendState(c?: PolicyContent): FamilyState {
  const s = (c as ExtendedContent | undefined)?.spend;
  if (!s?.enable) return "off";
  return s.remediate ? "enforcing" : "detect";
}
// Agent-hook remediate is a tri-state ("observe"|"block"|"approval"); both
// block and approval are active-enforcement modes for the summary tag, observe
// is detect-only. guardEnforcing collapses that onto the 2-way used by the
// coverage strip / section header.
type AgentGuard = { enable?: boolean; remediate?: AgentRemediate };
type GuardMode = "off" | "observe" | "block" | "approval";
function guardEnforcing(g?: AgentGuard): boolean {
  return g?.remediate === "block" || g?.remediate === "approval";
}
function guardMode(g?: AgentGuard): GuardMode {
  if (!g?.enable) return "off";
  return g.remediate === "block" ? "block" : g.remediate === "approval" ? "approval" : "observe";
}
// setGuardMode mutates a guard draft in place to reflect the chosen mode. Off
// only clears enable (keeps denied/patterns so re-enabling restores them).
function setGuardMode(g: AgentGuard, m: GuardMode) {
  if (m === "off") {
    g.enable = false;
    return;
  }
  g.enable = true;
  g.remediate = m === "observe" ? "observe" : m;
}
const GUARD_MODE_OPTIONS = [
  { label: "Off", value: "off" },
  { label: "Observe", value: "observe" },
  { label: "Block", value: "block" },
  { label: "Approval", value: "approval" },
] as const;
function guardModeTagState(m: GuardMode): FamilyState {
  return m === "off" ? "off" : m === "observe" ? "detect" : "enforcing";
}

function agentToolsState(c?: PolicyContent): FamilyState {
  const ext = c as ExtendedContent | undefined;
  const enabled = AGENT_SENSORS.flatMap(([k]) => {
    const s = ext?.[k];
    return [s?.tool_guard, k === "claude_sensor" ? s?.command_guard : undefined];
  }).filter((g): g is AgentGuard => Boolean(g?.enable));
  if (enabled.length === 0) return "off";
  return enabled.some((g) => guardEnforcing(g)) ? "enforcing" : "detect";
}

// familyStates — one entry per sensor family, for the coverage strip.
function familyStates(c?: PolicyContent): FamilyState[] {
  const mcp = c?.mcp;
  const brace = c?.brace;
  const mcpTools: FamilyState =
    !mcp?.servers || Object.keys(mcp.servers).length === 0
      ? "off"
      : mcp.remediate === false
        ? "detect"
        : "enforcing";
  const launch: FamilyState =
    [mcp?.default_stdio_policy, mcp?.default_sse_policy, mcp?.default_http_policy].some((p) => p === "deny")
      ? "enforcing"
      : "off";
  return [
    mcpTools,
    launch,
    toggleState(brace?.filter_regex),
    toggleState(brace?.process_guard),
    toggleState(brace?.sensitive_file_access),
    brace?.syscalls?.enable ? (brace.syscalls.mmap_exec?.remediate ? "enforcing" : "detect") : "off",
    brace?.network?.enable ? "enforcing" : "off",
    agentToolsState(c),
    modelsState(c),
    spendState(c),
  ];
}

// ---- Page ----------------------------------------------------------------

// Edit is not a separate mode — it happens inline in the PolicyDetail (the
// guided section editor). Only the create dialog is a modal.
type Mode = { kind: "list" } | { kind: "create" };

const TOP_N = 3;

export default function PolicyPage() {
  const { token } = theme.useToken();
  const [mode, setMode] = useState<Mode>({ kind: "list" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Freshly-created policy → its detail opens straight in guided edit mode.
  const [autoEditId, setAutoEditId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  // A ?policy=<tier|name> deep-link (e.g. clicking a sandbox's tier on the Fleet
  // page) — held here until policies load, then resolved to an id below.
  const [pendingSelect, setPendingSelect] = useState<string | null>(null);
  // Auto-paginate: `policies` below is a "show everything" list (the
  // `showAll` toggle just reveals/collapses what's already fetched locally)
  // — silently stopping at the server's default limit=50 would hide real
  // policies with no indication more exist. Capped at 20 pages (2000
  // policies at limit=100) as a defensive guard against a backend bug that
  // never stops returning next_cursor.
  const { data, error, loading, refetch } = useFetch<{ items: Policy[] }>(async (api) => {
    const items: Policy[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < 20; page++) {
      const res = await api.GET("/policies", { params: { query: { cursor, limit: 100 } } });
      if (res.error) return res;
      items.push(...(res.data?.items ?? []));
      cursor = res.data?.next_cursor;
      if (!cursor) return { data: { items }, error: undefined, response: res.response };
    }
    return { data: { items }, error: undefined, response: new Response() };
  });

  // Closed loop: a proposal arriving from Discovery (?propose=...) stages a
  // pre-filled edit on the ACTIVE policy; a ?policy=<tier|name> deep-link
  // selects that policy's detail. Both consumed once, then cleared from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  useEffect(() => {
    const p = parseProposal(searchParams);
    const want = searchParams.get("policy");
    if (p) {
      setProposal(p);
      setSelectedId(null); // detail defaults to the active policy
      setSearchParams({}, { replace: true });
    } else if (want) {
      setPendingSelect(want);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Active first, then tiers, then most recently updated.
  const policies = useMemo(() => {
    const items = [...(data?.items ?? [])];
    return items.sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      if (Boolean(a.tier_name) !== Boolean(b.tier_name)) return a.tier_name ? -1 : 1;
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
  }, [data]);

  // Resolve a pending ?policy=<tier|name> deep-link once policies have loaded.
  useEffect(() => {
    if (!pendingSelect || policies.length === 0) return;
    const match = policies.find((p) => p.tier_name === pendingSelect || p.name === pendingSelect);
    if (match) setSelectedId(match.id);
    setPendingSelect(null);
  }, [pendingSelect, policies]);

  const active = policies.find((p) => p.active);
  const selected = policies.find((p) => p.id === selectedId) ?? active ?? policies[0];
  const visible = showAll ? policies : policies.slice(0, TOP_N);
  const hidden = policies.length - TOP_N;

  const states = familyStates(active?.content);
  const enforcingCount = states.filter((s) => s === "enforcing").length;

  const kpiCardStyle = { flex: 1, minWidth: 200 } as const;
  const kpiBody = { body: { padding: `${token.paddingSM}px ${token.padding}px` } };
  const kpiValue = { fontSize: 22, fontWeight: 600 } as const;

  return (
    <Flex vertical gap="middle">
      <PageHeader
        title="Policy"
        subtitle="What each policy allows, blocks, and watches — per enforcement area."
        actions={
          <Button type="primary" onClick={() => setMode({ kind: "create" })}>
            Create policy
          </Button>
        }
      />

      {/* Posture row — from policy data only */}
      <Flex gap={16} wrap="wrap" align="stretch">
        <Card
          style={{ ...kpiCardStyle, cursor: active ? "pointer" : undefined }}
          styles={kpiBody}
          onClick={active ? () => setSelectedId(active.id) : undefined}
        >
          <Statistic
            title={
              <KpiTitle label="Active policy" hint="The policy compiled into every sandbox's enforcement config" />
            }
            value={loading ? "—" : active ? active.name : "—"}
            styles={{ content: kpiValue }}
            suffix={
              <Text type="secondary" className="text-xs">
                {active ? `v${active.version}` : "none active"}
              </Text>
            }
          />
        </Card>
        <Card style={kpiCardStyle} styles={kpiBody}>
          <Statistic
            title={
              <KpiTitle
                label="Mode"
                hint="Enforce blocks violations in-kernel; Observe logs them without blocking"
              />
            }
            value={
              loading
                ? "—"
                : active
                  ? enforcementMode(active.content) === "Enforce"
                    ? "Enforcing"
                    : "Observe"
                  : "—"
            }
            styles={{
              content: {
                ...kpiValue,
                color:
                  active && enforcementMode(active.content) === "Enforce"
                    ? TOKENS.governance
                    : undefined,
              },
            }}
            suffix={
              active ? (
                <Text type="secondary" className="text-xs">
                  {enforcingCount} of {states.length} areas
                </Text>
              ) : undefined
            }
          />
        </Card>
        <Card style={kpiCardStyle} styles={kpiBody}>
          <Statistic
            title={<KpiTitle label="Policies" hint="All policies in the store, including tier policies" />}
            value={loading ? "—" : policies.length}
            styles={{ content: kpiValue }}
            suffix={
              <Text type="secondary" className="text-xs">
                {policies.filter((p) => p.tier_name).length} tier
              </Text>
            }
          />
        </Card>
        <Card style={kpiCardStyle} styles={kpiBody}>
          <Statistic
            title={
              <KpiTitle
                label="Last change"
                hint="Most recent edit to any policy — full history is in the audit log"
              />
            }
            value={
              loading || policies.length === 0
                ? "—"
                : new Date(
                    Math.max(...policies.map((p) => new Date(p.updated_at).getTime())),
                  ).toLocaleDateString()
            }
            styles={{ content: kpiValue }}
          />
        </Card>
      </Flex>

      {/* All policies — top 3, expander for the rest */}
      <Card
        styles={{ body: { padding: 0 } }}
        title={
          <SectionHeading hint="One policy is active fleet-wide; tier policies are always deployed and selected per-container. Select a row to read it below.">
            Policies
          </SectionHeading>
        }
      >
        <div style={{ padding: "4px 24px 8px" }}>
          {loading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : error ? (
            <Text type="danger">{error}</Text>
          ) : policies.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  No policies yet. Select <strong>Create policy</strong> to get started.
                </span>
              }
            />
          ) : (
            <div className="divide-y" style={{ borderColor: token.colorBorderSecondary }}>
              {visible.map((p) => (
                <PolicyRow
                  key={p.id}
                  policy={p}
                  selected={selected?.id === p.id}
                  onSelect={() => setSelectedId(p.id)}
                  onEdit={() => {
                    setSelectedId(p.id);
                    setAutoEditId(p.id);
                  }}
                  onChange={refetch}
                />
              ))}
            </div>
          )}
        </div>
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              padding: "8px 24px",
              fontSize: 12,
              color: token.colorTextSecondary,
              background: "transparent",
              cursor: "pointer",
            }}
          >
            {showAll ? (
              <>
                <ChevronUp size={13} aria-hidden /> Show top {TOP_N}
              </>
            ) : (
              <>
                <ChevronDown size={13} aria-hidden /> Show all {policies.length}
              </>
            )}
          </button>
        )}
      </Card>

      {/* Selected policy, rendered per enforcement area */}
      {selected && (
        <PolicyDetail
          policy={selected}
          proposal={selected.active ? proposal : null}
          onProposalConsumed={() => setProposal(null)}
          autoEdit={selected.id === autoEditId}
          onAutoEditConsumed={() => setAutoEditId(null)}
          onSaved={refetch}
        />
      )}

      {mode.kind === "create" && (
        <CreatePolicyModal
          policies={policies}
          onClose={() => setMode({ kind: "list" })}
          onCreated={(p) => {
            setMode({ kind: "list" });
            setSelectedId(p.id);
            setAutoEditId(p.id);
            refetch();
          }}
        />
      )}
    </Flex>
  );
}

// KpiTitle — a Statistic title with an info hint, matching the cost
// dashboard's CostKpiStrip pattern. The hint icon itself is agate.tsx's
// HintIcon — this and SectionHeading below were their own separate
// Popover-wrapped-Info implementations before consolidating onto the one
// shared version (agate.tsx's SectionTitle/StatCard had two more, on
// Tooltip instead of Popover — four hand-rolled variants of the same
// pattern, now one).
function KpiTitle({ label, hint }: { label: string; hint: string }) {
  return (
    <Flex align="center" gap={4}>
      <span>{label}</span>
      <HintIcon hint={hint} maxWidth={280} />
    </Flex>
  );
}

// SectionHeading — a Card title with an optional info hint.
function SectionHeading({ hint, children }: { hint?: string; children: React.ReactNode }) {
  return (
    <Flex align="center" gap={6}>
      <span style={{ fontFamily: "var(--font-serif, 'Source Serif 4 Variable', serif)" }}>{children}</span>
      {hint && <HintIcon hint={hint} />}
    </Flex>
  );
}

// ---- Policy detail — the sectioned reader / guided editor ----------------

function PolicyDetail({
  policy,
  proposal,
  onProposalConsumed,
  autoEdit,
  onAutoEditConsumed,
  onSaved,
}: {
  policy: Policy;
  proposal: Proposal | null;
  onProposalConsumed: () => void;
  autoEdit?: boolean;
  onAutoEditConsumed?: () => void;
  onSaved: () => void;
}) {
  const { token } = theme.useToken();
  const api = useApi();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PolicyContent>(policy.content);
  const [staged, setStaged] = useState<Proposal | null>(null);
  // Agent-containment overview starts collapsed — cleaner page, better demo reveal.
  const [containmentOpen, setContainmentOpen] = useState(false);
  // Per-app sandbox scope (BRU app_policies): "default" = the root content;
  // named scopes live under content.app_scopes. Kernel rules are NOT scoped.
  const [scope, setScope] = useState("default");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Which control surface the right pane is editing (master-detail).
  const [surfaceKey, setSurfaceKey] = useState("models");

  // A different policy selected → leave edit mode.
  useEffect(() => {
    setEditing(false);
    setStaged(null);
    setScope("default");
    setErr(null);
  }, [policy.id]);

  // Fresh from the create dialog → open in guided edit mode immediately;
  // creation IS guided editing of a new draft.
  useEffect(() => {
    if (!autoEdit) return;
    setDraft(JSON.parse(JSON.stringify(policy.content)));
    setEditing(true);
    setErr(null);
    onAutoEditConsumed?.();
  }, [autoEdit, policy.content, onAutoEditConsumed]);

  // Proposal from Discovery → enter edit mode with the rule pre-staged.
  useEffect(() => {
    if (!proposal) return;
    const d = JSON.parse(JSON.stringify(policy.content)) as ExtendedContent;
    if (proposal.kind === "block-server") {
      d.mcp ??= {};
      d.mcp.servers ??= {};
      d.mcp.servers[proposal.arg] = {
        ...(d.mcp.servers[proposal.arg] ?? { transport: "stdio" }),
        deny_list: ["*"],
      };
    } else if (proposal.kind === "block-model") {
      const m = (d.models ??= {});
      if (m.mode === "allowlist") {
        m.models = (m.models ?? []).filter((x) => x !== proposal.arg);
      } else {
        m.mode = "blocklist";
        m.models = [...new Set([...(m.models ?? []), proposal.arg])];
        m.on_violation ??= "downgrade";
        m.downgrade_to ??= "claude-sonnet-5";
      }
    } else if (proposal.kind === "spend-cap") {
      const s = (d.spend ??= {});
      s.enable = true;
      s.remediate = true;
      s.session_usd ??= 15;
      s.user_daily_usd ??= 40;
      s.on_breach = s.on_breach && s.on_breach !== "alert" ? s.on_breach : "downgrade";
      s.downgrade_to ??= "claude-sonnet-5";
    }
    setDraft(d);
    setEditing(true);
    setStaged(proposal);
    setErr(null);
    onProposalConsumed();
  }, [proposal, policy.content, onProposalConsumed]);

  const content = editing ? draft : policy.content;
  const dirty = editing && JSON.stringify(draft) !== JSON.stringify(policy.content);
  const rootExt = content as ExtendedContent;
  const scopeNames = ["default", ...Object.keys(rootExt.app_scopes ?? {})];

  // update mutates a fresh clone of the draft, TARGETING THE SELECTED APP
  // SCOPE. Kernel rules use updateRoot (never scoped).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const update = (mut: (d: any) => void) =>
    setDraft((prev) => {
      const d = JSON.parse(JSON.stringify(prev ?? {})) as ExtendedContent;
      mut(scope === "default" ? d : ((d.app_scopes ??= {})[scope] ??= {}));
      return d;
    });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateRoot = (mut: (d: any) => void) =>
    setDraft((prev) => {
      const d = JSON.parse(JSON.stringify(prev ?? {}));
      mut(d);
      return d;
    });

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(policy.content)));
    setErr(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setStaged(null);
    setErr(null);
  };
  const save = async () => {
    setSaving(true);
    setErr(null);
    const res = await api.PUT("/policies/{policyId}", {
      params: { path: { policyId: policy.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { description: policy.description, content: draft },
    });
    setSaving(false);
    if (res.error) {
      setErr(describeError(res.error, res.response.status));
      return;
    }
    setEditing(false);
    setStaged(null);
    onSaved();
  };

  // All sandbox sections read the SELECTED SCOPE's content; kernel rules
  // read the root regardless of scope.
  const c = (scope === "default" ? content : (rootExt.app_scopes?.[scope] ?? {})) as PolicyContent;
  const ext = c as ExtendedContent;
  const mdl = ext.models;
  const spd = ext.spend;
  const mdlState = modelsState(c);
  const spdState = spendState(c);
  const mcp = c.mcp;
  const pg = c.brace?.process_guard;
  const sfa = c.brace?.sensitive_file_access;
  const net = c.brace?.network;
  const fr = c.brace?.filter_regex;
  const sys = c.brace?.syscalls;

  const states = familyStates(c);
  const enforcing = states.filter((s) => s === "enforcing").length;
  const detect = states.filter((s) => s === "detect").length;
  const off = states.filter((s) => s === "off").length;

  const mcpState: FamilyState =
    !mcp?.servers || Object.keys(mcp.servers).length === 0
      ? "off"
      : mcp.remediate === false
        ? "detect"
        : "enforcing";

  // --- section setters (build missing structure so a section can be
  // switched on from "off") ---
  const setEnableRemediate = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pick: (d: any) => any,
    k: string,
  ) =>
    update((d) => {
      const node = pick(d);
      if (k === "off") node.enable = false;
      else {
        node.enable = true;
        node.remediate = k === "enforce";
      }
    });
  const pgPick = (d: Record<string, Record<string, unknown>>) =>
    ((d.brace ??= {}).process_guard ??= { enable: false, remediate: false, allow: [], deny: [] });
  const sfaPick = (d: Record<string, Record<string, unknown>>) =>
    ((d.brace ??= {}).sensitive_file_access ??= { enable: false, remediate: false, paths: [] });
  const frPick = (d: Record<string, Record<string, unknown>>) =>
    ((d.brace ??= {}).filter_regex ??= { enable: false, remediate: false });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setList = (pick: (d: any) => any, field: string, items: unknown[]) =>
    update((d) => {
      pick(d)[field] = items;
    });

  // Per the BRU schema: among the brace syscall hooks only suspicious_syscalls
  // and mmap_exec block. open/socket are observational, connect's remediate is
  // ignored, exec/delete carry no remediation field.
  const OBS: { k: string; canBlock: boolean }[] = [
    { k: "exec", canBlock: false },
    { k: "open", canBlock: false },
    { k: "socket", canBlock: false },
    { k: "connect", canBlock: false },
    { k: "delete", canBlock: false },
    { k: "mmap_exec", canBlock: true },
    { k: "suspicious_syscalls", canBlock: true },
  ];
  const obsState = (k: string): "off" | "observe" | "block" => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const o = (sys as any)?.[k];
    if (!o?.enable) return "off";
    return o.remediate ? "block" : "observe";
  };
  const obsSet = (k: string, next: "off" | "observe" | "block") =>
    update((d) => {
      const sc = ((d.brace ??= {}).syscalls ??= { enable: true });
      sc.enable = true;
      const cur = (sc[k] ??= {});
      if (next === "off") cur.enable = false;
      else {
        cur.enable = true;
        cur.remediate = next === "block";
      }
    });

  // Deep-link into the Advisor's policy-authoring session.
  const advisorHref = `https://${window.location.hostname}:8501/?workflow=policy-update&theme=light`;

  // ── Control surfaces (master-detail) ────────────────────────────────────
  // Each surface maps 1:1 to an existing per-area editor rendered below in
  // renderSurfaceBody(); `state` drives the left-pane posture glyph. Host-wide
  // sensors own their own state (separate endpoint) so they carry no glyph.
  type Surface = {
    key: string;
    name: string;
    ruleKey: string;
    desc: string;
    icon: LucideIcon;
    group: string;
    state?: FamilyState;
  };
  // Ordered as the customer's adoption journey (top-to-bottom = the order you
  // configure it), while staying click-to-any. `group` draws the dividers.
  const surfaces: Surface[] = [
    {
      key: "models",
      name: "Model pinning",
      ruleKey: "warden.model_pin",
      desc: "Pin which models the sandbox may call; downgrade or block the rest.",
      icon: Pin,
      group: "Guided setup",
      state: mdlState,
    },
    {
      key: "network",
      name: "Network egress",
      ruleKey: "brace.network",
      desc: "Default-deny egress — only listed destinations leave the sandbox.",
      icon: Globe,
      group: "Guided setup",
      state: net?.enable ? "enforcing" : "off",
    },
    {
      key: "agent-tools",
      name: "Agent hooks",
      ruleKey: "claude_sensor.tool_guard",
      desc: "PreToolUse/BeforeTool tool-guard hooks per coding agent — Claude Code, Codex, Gemini, Cursor.",
      icon: Bot,
      group: "Guided setup",
      state: agentToolsState(c),
    },
    {
      key: "files",
      name: "Sensitive files",
      ruleKey: "brace.sensitive_file_access",
      desc: "Guard sensitive paths from read or write inside the sandbox.",
      icon: FileLock2,
      group: "Guided setup",
      state: toggleState(sfa),
    },
    {
      key: "mcp",
      name: "MCP filtering",
      ruleKey: "agentic_sensor.mcp",
      desc: "Per-server tool allow/deny and default transport posture (stdio/http/sse).",
      icon: Plug,
      group: "Guided setup",
      state: mcpState,
    },
    {
      key: "processes",
      name: "Process guard",
      ruleKey: "brace.process_guard",
      desc: "Allow/deny commands at execve — precise, but high-maintenance at fleet scale.",
      icon: Terminal,
      group: "Guided setup",
      state: toggleState(pg),
    },
    {
      key: "content",
      name: "Content filters",
      ruleKey: "brace.filter_regex",
      desc: "Regex content filters over payloads crossing the sandbox boundary — arguments, descriptions, and responses.",
      icon: Filter,
      group: "More controls",
      state: toggleState(fr),
    },
    {
      key: "spend",
      name: "Spend limits",
      ruleKey: "warden.spend",
      desc: "Session and per-user daily spend ceilings with breach handling.",
      icon: DollarSign,
      group: "More controls",
      state: spdState,
    },
    {
      key: "kernel",
      name: "Kernel observers",
      ruleKey: "brace.syscalls",
      desc: "Kernel syscall observers — exec, open, socket, mmap, and more.",
      icon: Activity,
      group: "More controls",
      state: sys?.enable ? (sys.mmap_exec?.remediate ? "enforcing" : "detect") : "off",
    },
    {
      key: "host",
      name: "Host-wide sensors",
      ruleKey: "kernel_sensor",
      desc: "Host-global kernel sensors — configured fleet-wide, not per policy.",
      icon: Server,
      group: "Advanced",
    },
  ];
  const activeSurface = surfaces.find((s) => s.key === surfaceKey) ?? surfaces[0];

  // renderSurfaceBody — the existing per-area editor JSX, keyed by surface.
  // These blocks are moved verbatim from the former stacked layout; the
  // <Section> wrapper still renders each area's title, mode control, and body.
  const renderSurfaceBody = (key: string): React.ReactNode => {
    switch (key) {
      case "network":
        return (
          <Section
            title="Network"
            area="network"
            state={net?.enable ? "enforcing" : "off"}
            editing={editing}
            offNote="Unrestricted — sandboxes may reach any destination."
            modeNote={net?.enable ? "Default-deny: anything not listed below is dropped." : undefined}
            control={
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                size="small"
                value={net?.enable ? "enforce" : "off"}
                onChange={(e) =>
                  update((d) => (((d.brace ??= {}).network ??= { enable: false, allow_to: [] }).enable = e.target.value === "enforce"))
                }
              >
                <Radio.Button value="off">Off</Radio.Button>
                <Radio.Button value="enforce">Enforce</Radio.Button>
              </Radio.Group>
            }
          >
            {editing ? (
              <NetworkProviderPicker
                rows={net?.allow_to ?? []}
                onChange={(rows) => update((d) => (((d.brace ??= {}).network ??= { enable: true, allow_to: [] }).allow_to = rows))}
              />
            ) : (
              <NetworkRows rows={net?.allow_to ?? []} editing={false} onChange={() => {}} />
            )}
          </Section>
        );
      case "files":
        return (
          <Section
            title="Files"
            area="files"
            state={toggleState(sfa)}
            editing={editing}
            offNote="No guarded paths — file access is not monitored."
            modeNote={
              sfa?.enable
                ? sfa.remediate
                  ? "Access to these paths is blocked (always inline)."
                  : "Observe mode — access violations are logged, nothing is blocked."
                : undefined
            }
            control={
              <PolicyModeControl
                size="small"
                value={toMode(toggleState(sfa))}
                onChange={(m) => setEnableRemediate(sfaPick, fromMode(m))}
                aria-label="Files enforcement mode"
              />
            }
          >
            <FieldLabel>Guarded paths</FieldLabel>
            {editing ? (
              <PathChips items={sfa?.paths ?? []} onChange={(it) => setList(sfaPick, "paths", it)} />
            ) : (sfa?.paths?.length ?? 0) === 0 ? (
              <Text type="secondary" style={{ fontSize: 12 }}>none</Text>
            ) : (
              <TagList items={(sfa?.paths ?? []).map((p) => (p.write_only ? `${p.path} (writes only)` : p.path))} tone="warning" />
            )}
          </Section>
        );
      case "processes":
        return (
          <Section
            title="Processes"
            area="processes"
            state={toggleState(pg)}
            editing={editing}
            offNote="No process rules — sandboxes may run any command."
            modeNote={
              pg?.enable
                ? pg.remediate
                  ? "Denied commands are blocked at execve (always inline)."
                  : "Observe mode — violations are logged, nothing is blocked."
                : undefined
            }
            control={
              <PolicyModeControl
                size="small"
                value={toMode(toggleState(pg))}
                onChange={(m) => setEnableRemediate(pgPick, fromMode(m))}
                aria-label="Processes enforcement mode"
              />
            }
          >
            <Row gutter={[16, 12]}>
              <Col xs={24} md={12}>
                <LabeledChips label="Blocked commands" items={pg?.deny ?? []} tone="danger" editing={editing} placeholder="command or glob" onChange={(it) => setList(pgPick, "deny", it)} />
              </Col>
              <Col xs={24} md={12}>
                <LabeledChips label="Always allowed" items={pg?.allow ?? []} editing={editing} placeholder="command" onChange={(it) => setList(pgPick, "allow", it)} />
              </Col>
            </Row>
            {pg?.deny_exceptions && pg.deny_exceptions.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: "block" }}>
                {pg.deny_exceptions.length} deny exception(s) — see the JSON editor for details.
              </Text>
            )}
          </Section>
        );
      case "kernel":
        return (
          <Section
            title="Kernel observers"
            area="kernel"
            state={sys?.enable ? (sys.mmap_exec?.remediate ? "enforcing" : "detect") : "off"}
            editing={editing}
            offNote="Kernel syscall observation is off."
            modeNote={editing ? "Set each observer off, observe, or block." : undefined}
            control={
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                size="small"
                value={sys?.enable ? "on" : "off"}
                onChange={(e) => update((d) => (((d.brace ??= {}).syscalls ??= { enable: false }).enable = e.target.value === "on"))}
              >
                <Radio.Button value="off">Off</Radio.Button>
                <Radio.Button value="on">On</Radio.Button>
              </Radio.Group>
            }
          >
            <Flex wrap="wrap" gap={12}>
              {OBS.map(({ k, canBlock }) => {
                const st = obsState(k);
                return (
                  <div key={k} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Text style={{ fontFamily: token.fontFamilyCode, fontSize: 12 }}>{k}</Text>
                    {editing ? (
                      <Segmented
                        size="small"
                        value={st}
                        onChange={(v) => obsSet(k, v as "off" | "observe" | "block")}
                        options={
                          canBlock
                            ? [
                                { label: "Off", value: "off" },
                                { label: "Observe", value: "observe" },
                                { label: "Block", value: "block" },
                              ]
                            : [
                                { label: "Off", value: "off" },
                                { label: "Observe", value: "observe" },
                              ]
                        }
                      />
                    ) : (
                      <Tag color={st === "block" ? TOKENS.danger : st === "observe" ? TOKENS.warning : "default"} style={{ marginInlineEnd: 0 }}>
                        {st}
                      </Tag>
                    )}
                  </div>
                );
              })}
            </Flex>
          </Section>
        );
      case "mcp":
        return (
          <Section
            title="MCP tools"
            area="mcp-tools"
            state={mcpState}
            editing={editing}
            highlight={staged?.kind === "block-server"}
            offNote="No MCP tool rules — every tool on every server is callable."
            control={
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                size="small"
                value={mcp?.remediate === false ? "alert" : "block"}
                onChange={(e) => update((d) => ((d.mcp ??= {}).remediate = e.target.value === "block"))}
              >
                <Radio.Button value="block">Block</Radio.Button>
                <Radio.Button value="alert">Alert</Radio.Button>
              </Radio.Group>
            }
          >
            <Flex wrap="wrap" align="center" gap={8} style={{ marginBottom: 12, fontSize: 12 }}>
              {(["stdio", "sse", "http"] as const).map((t) => {
                const deny = mcp?.[`default_${t}_policy`] === "deny";
                const label = `unlisted ${t} servers: ${deny ? "blocked" : "allowed"}`;
                return editing ? (
                  <Tag.CheckableTag
                    key={t}
                    checked={deny}
                    onChange={() => update((d) => ((d.mcp ??= {})[`default_${t}_policy`] = deny ? "allow" : "deny"))}
                    style={{
                      fontFamily: token.fontFamilyCode,
                      border: `1px solid ${deny ? TOKENS.danger : token.colorBorder}`,
                      color: deny ? TOKENS.danger : token.colorTextSecondary,
                      background: deny ? token.colorErrorBg : "transparent",
                    }}
                  >
                    {label}
                  </Tag.CheckableTag>
                ) : (
                  <Tag key={t} color={deny ? TOKENS.danger : "default"} style={{ fontFamily: token.fontFamilyCode }}>
                    {label}
                  </Tag>
                );
              })}
              {!editing && (
                <Tag style={{ fontFamily: token.fontFamilyCode }}>
                  on violation: {mcp?.remediate === false ? "alert only" : "block"}
                </Tag>
              )}
            </Flex>
            {mcp?.servers && Object.keys(mcp.servers).length > 0 && (
              <McpServerTable
                servers={mcp.servers}
                editing={editing}
                onServerChange={(name, patch) =>
                  update((d) => {
                    const s = ((d.mcp ??= {}).servers ??= {});
                    s[name] = { ...(s[name] ?? {}), ...patch };
                  })
                }
                onRemove={(name) => update((d) => d.mcp?.servers && delete d.mcp.servers[name])}
              />
            )}
            {editing && (
              <Button
                size="small"
                icon={<Plus size={13} />}
                style={{ marginTop: 12 }}
                onClick={() => {
                  const name = window.prompt("New MCP server name:")?.trim();
                  if (!name) return;
                  update((d) => {
                    const s = ((d.mcp ??= {}).servers ??= {});
                    if (!s[name]) s[name] = { allow_list: [], deny_list: [], transport: "stdio" };
                  });
                }}
              >
                Add server
              </Button>
            )}
            {/* Advanced per-server message rules (argument-level). Reuses the same
                server setter as the table; edits d.mcp.servers[name].message_filter. */}
            {mcp?.servers && Object.keys(mcp.servers).length > 0 && (
              <McpToolFilterEditor
                servers={mcp.servers as Record<string, ExtendedMcpServer>}
                editing={editing}
                onServerChange={(name, patch) =>
                  update((d) => {
                    const s = ((d.mcp ??= {}).servers ??= {});
                    s[name] = { ...(s[name] ?? {}), ...patch };
                  })
                }
              />
            )}
          </Section>
        );
      case "content":
        return (
          <Section
            title="Content filters"
            area="content-filters"
            state={toggleState(fr)}
            editing={editing}
            offNote="No content filters — payloads crossing the sandbox boundary are not inspected."
            modeNote={
              fr?.enable
                ? fr.remediate
                  ? "Matching content is blocked (always inline)."
                  : "Observe mode — matches are logged, nothing is blocked."
                : undefined
            }
            control={
              <PolicyModeControl
                size="small"
                value={toMode(toggleState(fr))}
                onChange={(m) => setEnableRemediate(frPick, fromMode(m))}
                aria-label="Content filters enforcement mode"
              />
            }
          >
            <Flex vertical gap={12}>
              <LabeledChips label="Tool argument patterns" items={fr?.tool_arg_patterns ?? []} tone="warning" editing={editing} placeholder="regex" onChange={(it) => setList(frPick, "tool_arg_patterns", it)} />
              <LabeledChips label="Tool description patterns" items={fr?.tool_description_patterns ?? []} tone="warning" editing={editing} placeholder="regex" onChange={(it) => setList(frPick, "tool_description_patterns", it)} />
              <LabeledChips label="Tool response patterns" items={fr?.tool_response_patterns ?? []} tone="warning" editing={editing} placeholder="regex" onChange={(it) => setList(frPick, "tool_response_patterns", it)} />
            </Flex>
          </Section>
        );
      case "agent-tools":
        return (
          <Section title="Agent hooks" area="agent-tools" state={agentToolsState(c)} editing={editing} offNote="No agent tool rules — coding agents may use every tool their harness offers.">
            <Flex vertical gap={12}>
              {AGENT_SENSORS.map(([key, label]) => {
                const sensor = ext[key];
                const g = sensor?.tool_guard;
                const tgMode = guardMode(g);
                const groups = AGENT_TOOL_CATALOG[key] ?? [];
                const total = agentToolCount(key);
                const denied = g?.denied ?? [];
                const governed = denied.filter((t) => t !== "MCP").length;
                // command_guard + attribution are Claude-only in the devel schema.
                const isClaude = key === "claude_sensor";
                const cg = isClaude ? sensor?.command_guard : undefined;
                const cgMode = guardMode(cg);
                const patterns = cg?.patterns ?? [];
                const tagToolUseID = sensor?.attribution?.tag_tool_use_id ?? true;
                const modeOptions = GUARD_MODE_OPTIONS.map((o) => ({ label: o.label, value: o.value }));
                return (
                  <div key={key} style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 10, padding: "10px 12px" }}>
                    <Flex align="center" justify="space-between" gap={12} wrap="wrap" style={{ marginBottom: 10 }}>
                      <span>
                        <Text strong style={{ fontSize: 13 }}>{label}</Text>
                        <Text type="secondary" style={{ fontSize: 11, marginInlineStart: 6 }}>
                          {governed} / {total} tools{denied.includes("MCP") ? " · MCP blocked" : ""}
                          {tgMode === "approval" ? " · approval" : ""}
                        </Text>
                      </span>
                      {editing ? (
                        <Segmented
                          size="small"
                          value={tgMode}
                          options={modeOptions}
                          onChange={(v) =>
                            update((d) => {
                              const tg = ((d[key] ??= {}).tool_guard ??= { enable: false, remediate: "observe", denied: [] });
                              setGuardMode(tg, v as GuardMode);
                            })
                          }
                          aria-label={`${label} tool guard mode`}
                        />
                      ) : (
                        <ModeTag state={guardModeTagState(tgMode)} />
                      )}
                    </Flex>
                    {/* Tool guard — click the specific tools this agent may not call. */}
                    {editing ? (
                      <AgentToolPicker
                        groups={groups}
                        denied={denied}
                        onChange={(it) =>
                          update((d) => {
                            const tg = ((d[key] ??= {}).tool_guard ??= { enable: true, remediate: "observe" });
                            tg.denied = it;
                          })
                        }
                      />
                    ) : denied.length > 0 ? (
                      <div>
                        <FieldLabel>Governed tools</FieldLabel>
                        <TagList items={denied} tone="danger" />
                      </div>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>No tools governed — every {label} tool is allowed.</Text>
                    )}
                    {/* Command guard + attribution — Claude-only (devel schema). */}
                    {isClaude && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${token.colorBorderSecondary}` }}>
                        <Flex align="center" justify="space-between" gap={12} wrap="wrap" style={{ marginBottom: 8 }}>
                          <span>
                            <Text strong style={{ fontSize: 12 }}>Command guard</Text>
                            <Text type="secondary" style={{ fontSize: 11, marginInlineStart: 6 }}>
                              dangerous-command patterns{cgMode === "approval" ? " · approval prompt" : ""}
                            </Text>
                          </span>
                          {editing ? (
                            <Segmented
                              size="small"
                              value={cgMode}
                              options={modeOptions}
                              onChange={(v) =>
                                update((d) => {
                                  const cmd = ((d[key] ??= {}).command_guard ??= { enable: false, remediate: "observe", patterns: [] });
                                  setGuardMode(cmd, v as GuardMode);
                                })
                              }
                              aria-label="Claude command guard mode"
                            />
                          ) : (
                            <ModeTag state={guardModeTagState(cgMode)} />
                          )}
                        </Flex>
                        {editing ? (
                          <LabeledChips
                            label="Command patterns"
                            items={patterns}
                            tone="danger"
                            editing={editing}
                            placeholder="e.g. git push -f"
                            onChange={(it) =>
                              update((d) => {
                                const cmd = ((d[key] ??= {}).command_guard ??= { enable: true, remediate: "observe", patterns: [] });
                                cmd.patterns = it;
                              })
                            }
                          />
                        ) : patterns.length > 0 ? (
                          <div>
                            <FieldLabel>Command patterns</FieldLabel>
                            <TagList items={patterns} tone="danger" />
                          </div>
                        ) : null}
                        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, cursor: editing ? "pointer" : "default" }}>
                          <Checkbox
                            checked={tagToolUseID}
                            disabled={!editing}
                            onChange={(e) =>
                              update((d) => {
                                const attr = ((d[key] ??= {}).attribution ??= {});
                                attr.tag_tool_use_id = e.target.checked;
                              })
                            }
                          />
                          <span>
                            <Text style={{ fontSize: 12 }}>Tag tool-use IDs for attribution</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              Prepend <Text code style={{ fontSize: 11 }}>AGENT_TOOL_USE_ID</Text> to each Bash command so the advisor can attribute file / network activity to the originating tool call.
                            </Text>
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </Flex>
          </Section>
        );
      case "models":
        return (
          <Section
            title="Models"
            area="models"
            state={mdlState}
            editing={editing}
            highlight={staged?.kind === "block-model"}
            offNote="No model rules — sandboxes may call any model."
            modeNote={
              mdl?.models?.length
                ? `${mdl.mode === "allowlist" ? "Only listed models may be called; other calls are" : "Calls to listed models are"} ${
                    mdl.on_violation === "downgrade"
                      ? `downgraded to ${mdl.downgrade_to ?? "a cheaper model"}`
                      : mdl.on_violation === "alert"
                        ? "logged, not blocked"
                        : "blocked"
                  }.`
                : undefined
            }
            control={
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                size="small"
                value={mdl?.models?.length ? (mdl.mode ?? "blocklist") : "off"}
                onChange={(e) =>
                  update((d) => {
                    const m = (d.models ??= {});
                    const k = e.target.value;
                    if (k === "off") m.models = [];
                    else {
                      m.mode = k;
                      if (!m.models?.length) m.models = k === "allowlist" ? ["claude-sonnet-5", "claude-haiku-4-5"] : [];
                      m.on_violation ??= "downgrade";
                      m.downgrade_to ??= "claude-sonnet-5";
                    }
                  })
                }
              >
                <Radio.Button value="off">Off</Radio.Button>
                <Radio.Button value="blocklist">Blocklist</Radio.Button>
                <Radio.Button value="allowlist">Allowlist</Radio.Button>
              </Radio.Group>
            }
          >
            <Flex vertical gap={12}>
              {editing ? (
                <ModelCatalogPicker
                  value={mdl?.models ?? []}
                  mode={(mdl?.mode ?? "blocklist") as "allowlist" | "blocklist"}
                  onChange={(it) =>
                    update((d) => {
                      const m = (d.models ??= {});
                      m.mode ??= "blocklist";
                      m.models = it;
                    })
                  }
                />
              ) : (
                <LabeledChips
                  label={mdl?.mode === "allowlist" ? "Approved models" : "Blocked models"}
                  items={mdl?.models ?? []}
                  tone={mdl?.mode === "allowlist" ? undefined : "danger"}
                  editing={false}
                />
              )}
              {editing ? (
                <Flex wrap="wrap" align="center" gap={12}>
                  <FieldLabel inline>On violation</FieldLabel>
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                    value={mdl?.on_violation ?? "block"}
                    onChange={(e) => update((d) => ((d.models ??= {}).on_violation = e.target.value))}
                  >
                    <Radio.Button value="block">Block</Radio.Button>
                    <Radio.Button value="downgrade">Downgrade</Radio.Button>
                    <Radio.Button value="alert">Alert</Radio.Button>
                  </Radio.Group>
                  {mdl?.on_violation === "downgrade" && (
                    <Flex align="center" gap={6}>
                      <Text type="secondary" style={{ fontSize: 12 }}>to</Text>
                      <Input
                        size="small"
                        style={{ width: 180, fontFamily: token.fontFamilyCode }}
                        value={mdl?.downgrade_to ?? ""}
                        onChange={(e) => update((d) => ((d.models ??= {}).downgrade_to = e.target.value))}
                        placeholder="claude-sonnet-5"
                      />
                    </Flex>
                  )}
                </Flex>
              ) : (
                mdl?.on_violation === "downgrade" && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Downgrade preserves the agent's task — the call is re-routed to <code>{mdl.downgrade_to}</code> instead of failing.
                  </Text>
                )
              )}
            </Flex>
          </Section>
        );
      case "spend":
        return (
          <Section
            title="Spend limits"
            area="spend"
            state={spdState}
            editing={editing}
            highlight={staged?.kind === "spend-cap"}
            offNote="No spend guardrails — sessions and users have no cost ceiling."
            modeNote={
              spd?.enable
                ? spd.remediate
                  ? `Breaches are ${spd.on_breach === "downgrade" ? `downgraded to ${spd.downgrade_to ?? "a cheaper model"}` : spd.on_breach === "alert" ? "alerted" : "blocked"} in-session.`
                  : "Breaches are logged and alerted — nothing is blocked."
                : undefined
            }
            control={
              <PolicyModeControl
                size="small"
                value={toMode(spdState)}
                onChange={(m) =>
                  update((d) => {
                    const s = (d.spend ??= {});
                    const k = fromMode(m);
                    if (k === "off") s.enable = false;
                    else {
                      s.enable = true;
                      s.remediate = k === "enforce";
                      s.session_usd ??= 15;
                      s.user_daily_usd ??= 40;
                      s.on_breach ??= "downgrade";
                      s.downgrade_to ??= "claude-sonnet-5";
                    }
                  })
                }
                aria-label="Spend limits enforcement mode"
              />
            }
          >
            <Flex wrap="wrap" align="center" gap={24}>
              <Flex align="center" gap={8}>
                <FieldLabel inline>Per session</FieldLabel>
                {editing ? (
                  <InputNumber
                    size="small"
                    min={0}
                    prefix="$"
                    style={{ width: 110 }}
                    value={spd?.session_usd ?? null}
                    onChange={(v) => update((d) => ((d.spend ??= {}).session_usd = v == null ? undefined : Number(v)))}
                  />
                ) : (
                  <Text style={{ fontFamily: token.fontFamilyCode, fontSize: 12 }}>
                    {spd?.session_usd != null ? `$${spd.session_usd}` : "—"}
                  </Text>
                )}
              </Flex>
              <Flex align="center" gap={8}>
                <FieldLabel inline>Per user / day</FieldLabel>
                {editing ? (
                  <InputNumber
                    size="small"
                    min={0}
                    prefix="$"
                    style={{ width: 110 }}
                    value={spd?.user_daily_usd ?? null}
                    onChange={(v) => update((d) => ((d.spend ??= {}).user_daily_usd = v == null ? undefined : Number(v)))}
                  />
                ) : (
                  <Text style={{ fontFamily: token.fontFamilyCode, fontSize: 12 }}>
                    {spd?.user_daily_usd != null ? `$${spd.user_daily_usd}` : "—"}
                  </Text>
                )}
              </Flex>
              {editing && spd?.remediate && (
                <Flex align="center" gap={8}>
                  <FieldLabel inline>On breach</FieldLabel>
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                    value={spd?.on_breach ?? "downgrade"}
                    onChange={(e) => update((d) => ((d.spend ??= {}).on_breach = e.target.value))}
                  >
                    <Radio.Button value="block">Block</Radio.Button>
                    <Radio.Button value="downgrade">Downgrade</Radio.Button>
                    <Radio.Button value="alert">Alert</Radio.Button>
                  </Radio.Group>
                  {spd?.on_breach === "downgrade" && (
                    <Flex align="center" gap={6}>
                      <Text type="secondary" style={{ fontSize: 12 }}>to</Text>
                      <Input
                        size="small"
                        style={{ width: 180, fontFamily: token.fontFamilyCode }}
                        value={spd?.downgrade_to ?? ""}
                        onChange={(e) => update((d) => ((d.spend ??= {}).downgrade_to = e.target.value))}
                        placeholder="claude-sonnet-5"
                      />
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>
          </Section>
        );
      case "host":
        return <HostSensorsSection editing={editing} />;
      default:
        return null;
    }
  };

  return (
    <Card styles={{ body: { padding: 0 } }}>
      <div className="head" style={{ padding: "20px 24px 16px" }}>
        <Flex align="flex-start" justify="space-between" gap={16} wrap="wrap">
          <div style={{ minWidth: 0 }}>
            <div
              className="text-governance"
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: TOKENS.governance,
                marginBottom: 4,
              }}
            >
              Govern · Policy
            </div>
            <Flex align="center" gap={10} wrap="wrap">
              <span
                style={{
                  fontFamily: "var(--font-serif, 'Source Serif 4 Variable', serif)",
                  fontSize: 22,
                  fontWeight: 600,
                  color: token.colorText,
                }}
              >
                {editing ? "Editing policy" : "Sandbox policy"}
              </span>
              <span
                style={{
                  fontFamily: token.fontFamilyCode,
                  fontSize: 12,
                  color: token.colorTextSecondary,
                  border: `1px solid ${token.colorBorderSecondary}`,
                  borderRadius: 999,
                  padding: "2px 10px",
                }}
              >
                {policy.name} · v{policy.version}
              </span>
              {policy.active && <Tag color={TOKENS.governance}>Active</Tag>}
            </Flex>
            {policy.description && (
              <Text type="secondary" style={{ display: "block", marginTop: 6, maxWidth: 640 }}>
                {policy.description}
              </Text>
            )}
            <Text
              type="secondary"
              style={{ display: "block", marginTop: 8, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Layering — {enforcing} blocking · {detect} observing · {off} off
            </Text>
          </div>
          <Flex align="center" gap={8} wrap="wrap">
            <a
              href={advisorHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}
              title="Open the BlueRock Advisor — it drafts policy from observed activity"
            >
              <Sparkles size={12} aria-hidden /> Ask the Advisor
            </a>
            {!editing ? (
              <Button size="small" onClick={startEdit}>
                Edit policy
              </Button>
            ) : (
              <>
                <Button size="small" onClick={cancel} disabled={saving}>
                  Discard
                </Button>
                <Button type="primary" size="small" onClick={save} disabled={!dirty || saving} loading={saving}>
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </>
            )}
          </Flex>
        </Flex>
        {staged && (
          <div
            style={{
              margin: "12px 0",
              borderRadius: 8,
              border: `1px solid ${token.colorPrimaryBorder}`,
              background: token.colorPrimaryBg,
              padding: "8px 12px",
              fontSize: token.fontSizeSM,
            }}
          >
            <Text strong>
              Staged from Discovery:{" "}
              {staged.kind === "block-server" && (
                <>
                  block every tool on <code>{staged.arg}</code>
                </>
              )}
              {staged.kind === "block-model" && (
                <>
                  govern model <code>{staged.arg}</code>
                </>
              )}
              {staged.kind === "spend-cap" && <>enforce spend caps</>}
            </Text>
            {staged.evidence && (
              <Text type="secondary" style={{ marginInlineStart: 8, fontSize: 12 }}>
                — {staged.evidence}
              </Text>
            )}
            <Text type="secondary" style={{ marginInlineStart: 8, fontSize: 12 }}>
              Review the highlighted change below, then save or discard.
            </Text>
          </div>
        )}
      </div>

      {/* ── SANDBOX — per-app scopes ─────────────────────────────────── */}
      <Flex
        wrap="wrap"
        align="center"
        justify="space-between"
        gap={12}
        style={{
          marginTop: 16,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          background: token.colorFillQuaternary,
          padding: "10px 24px",
        }}
      >
        <div>
          <Text strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Sandbox — per-app policies
          </Text>
          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
            Enforced inside BRace sandboxes. Each app scope carries its own rules; <code>default</code> covers
            everything without a named scope.
          </Text>
        </div>
        <Flex wrap="wrap" align="center" gap={6}>
          {scopeNames.map((name) => (
            <span key={name} style={{ display: "inline-flex", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => setScope(name)}
                style={{
                  borderRadius: 999,
                  padding: "4px 12px",
                  fontFamily: token.fontFamilyCode,
                  fontSize: 12,
                  cursor: "pointer",
                  border: scope === name ? "none" : `1px solid ${token.colorBorder}`,
                  background: scope === name ? token.colorPrimary : token.colorBgContainer,
                  color: scope === name ? "#fff" : token.colorTextSecondary,
                  fontWeight: scope === name ? 500 : 400,
                }}
              >
                {name}
              </button>
              {editing && name !== "default" && (
                <button
                  type="button"
                  aria-label={`remove scope ${name}`}
                  onClick={() => {
                    if (!confirm(`Remove app scope "${name}"? Apps using it fall back to default.`)) return;
                    updateRoot((d) => d.app_scopes && delete d.app_scopes[name]);
                    if (scope === name) setScope("default");
                  }}
                  style={{ marginInlineStart: 2, border: "none", background: "none", cursor: "pointer", color: token.colorTextTertiary }}
                >
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
          {editing && (
            <button
              type="button"
              onClick={() => {
                const name = window.prompt("New app scope name (e.g. ai-dev, billing-svc):")?.trim();
                if (!name || name === "default") return;
                updateRoot((d) => ((d.app_scopes ??= {})[name] ??= {}));
                setScope(name);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                borderRadius: 999,
                border: `1px solid ${token.colorBorder}`,
                padding: "4px 10px",
                fontSize: 12,
                background: "none",
                cursor: "pointer",
                color: token.colorTextSecondary,
              }}
            >
              <Plus size={12} /> scope
            </button>
          )}
        </Flex>
      </Flex>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px minmax(0, 1fr)",
          gap: 16,
          padding: "24px 24px 24px",
          alignItems: "start",
        }}
      >
        {/* LEFT — control surfaces */}
        <div
          style={{
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: 12,
            background: token.colorBgContainer,
            minHeight: 520,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 14px",
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              fontFamily: "var(--font-serif, 'Source Serif 4 Variable', serif)",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Control surfaces
          </div>
          <div style={{ padding: "6px 6px 10px", maxHeight: 620, overflowY: "auto" }}>
            {surfaces.map((s, i) => {
              const Icon = s.icon;
              const isActive = s.key === activeSurface.key;
              const newGroup = i === 0 || surfaces[i - 1].group !== s.group;
              return (
                <div key={s.key}>
                  {newGroup && (
                    <div
                      style={{
                        padding: "10px 10px 4px",
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        color: token.colorTextTertiary,
                      }}
                    >
                      {s.group}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setSurfaceKey(s.key)}
                    aria-pressed={isActive}
                    className="surf"
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      textAlign: "left",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 8,
                      padding: "8px 10px",
                      background: isActive ? token.colorPrimaryBg : "transparent",
                    }}
                  >
                    <Icon
                      size={16}
                      aria-hidden
                      style={{ flexShrink: 0, color: isActive ? token.colorPrimary : token.colorTextSecondary }}
                    />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: isActive ? 600 : 500,
                          color: token.colorText,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.name}
                      </span>
                      <span
                        style={{
                          display: "block",
                          fontFamily: token.fontFamilyCode,
                          fontSize: 11,
                          color: token.colorTextTertiary,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.ruleKey}
                      </span>
                    </span>
                    {s.state ? (
                      <ModeTag state={s.state} />
                    ) : (
                      <span
                        style={{
                          fontSize: 10,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          color: token.colorTextTertiary,
                        }}
                      >
                        System
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — editor for the selected surface */}
        <div
          style={{
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: 12,
            background: token.colorBgContainer,
            minHeight: 520,
            overflow: "hidden",
          }}
        >
          <div className="ed-hero" style={{ padding: "18px 24px 4px" }}>
            <Flex align="center" justify="space-between" gap={12} wrap="wrap">
              <span
                style={{
                  fontFamily: "var(--font-serif, 'Source Serif 4 Variable', serif)",
                  fontSize: 18,
                  fontWeight: 600,
                  color: token.colorText,
                }}
              >
                {activeSurface.name}
              </span>
              {activeSurface.state && <ModeTag state={activeSurface.state} />}
            </Flex>
            {activeSurface.ruleKey && (
              <div
                className="text-governance"
                style={{ fontFamily: token.fontFamilyCode, fontSize: 12, color: TOKENS.governance, marginTop: 2 }}
              >
                {activeSurface.ruleKey}
              </div>
            )}
            {activeSurface.desc && (
              <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4, maxWidth: 640 }}>
                {activeSurface.desc}
              </Text>
            )}
          </div>
          {renderSurfaceBody(activeSurface.key)}
        </div>
      </div>

      {/* ── Agent containment — cross-cutting, exec-facing overview of the whole
          containment posture for this scope. Its own collapsible section under the
          policy's per-sensor surfaces; embeds the real network/mcp/files/syscall
          editors when editing (renderSurfaceBody is the single source of truth). ── */}
      <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, padding: "18px 24px 4px" }}>
        <button
          type="button"
          onClick={() => setContainmentOpen((v) => !v)}
          aria-expanded={containmentOpen}
          style={{
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            marginBottom: 14, width: "100%", background: "none", border: "none",
            padding: 0, cursor: "pointer", textAlign: "left",
          }}
        >
          <ChevronDown
            size={17}
            aria-hidden
            style={{ color: token.colorTextTertiary, transform: containmentOpen ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}
          />
          <ShieldHalf size={17} style={{ color: TOKENS.governance }} aria-hidden />
          <span style={{ fontFamily: "var(--font-serif, 'Source Serif 4 Variable', serif)", fontSize: 18, fontWeight: 600, color: token.colorText }}>
            Agent containment
          </span>
          <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: token.colorTextTertiary }}>System</span>
        </button>
        <AgentContainmentPanel editing={editing} content={c} update={update} renderEditor={renderSurfaceBody} collapsed={!containmentOpen} />
      </div>

      {editing && (
        <Flex
          align="center"
          justify="space-between"
          gap={12}
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 10,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorBgElevated,
            padding: "12px 24px",
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dirty ? "Unsaved changes to " : "No changes to "}
            <code>{policy.name}</code>
            {policy.active && (
              <Text style={{ color: TOKENS.warning, fontSize: 12 }}> · active policy — saving re-deploys to every sandbox</Text>
            )}
            {err && <Text type="danger" style={{ marginInlineStart: 8, fontSize: 12 }}>{err}</Text>}
          </Text>
          <Flex gap={8}>
            <Button size="small" onClick={cancel} disabled={saving}>
              Discard
            </Button>
            <Button type="primary" size="small" onClick={save} disabled={!dirty || saving} loading={saving}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </Flex>
        </Flex>
      )}
    </Card>
  );
}

// ---- Small presentational primitives -------------------------------------

function FieldLabel({ children, inline }: { children: React.ReactNode; inline?: boolean }) {
  return (
    <Text
      type="secondary"
      style={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        display: inline ? "inline" : "block",
        marginBottom: inline ? 0 : 6,
      }}
    >
      {children}
    </Text>
  );
}

function TagList({ items, tone }: { items: string[]; tone?: "danger" | "warning" }) {
  const color = tone === "danger" ? TOKENS.danger : tone === "warning" ? TOKENS.warning : undefined;
  return (
    <Flex wrap="wrap" gap={6}>
      {items.map((v, i) => (
        <Tag key={`${v}-${i}`} color={color} style={{ marginInlineEnd: 0, fontFamily: "var(--font-mono, monospace)" }}>
          {v}
        </Tag>
      ))}
    </Flex>
  );
}

function LabeledChips({
  label,
  items,
  tone,
  editing,
  onChange,
  placeholder,
}: {
  label: string;
  items: string[];
  tone?: "danger" | "warning";
  editing?: boolean;
  onChange?: (items: string[]) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      {editing && onChange ? (
        <ChipEditor value={items} onChange={onChange} placeholder={placeholder ? `${placeholder}, press Enter` : undefined} />
      ) : items.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>none</Text>
      ) : (
        <TagList items={items} tone={tone} />
      )}
    </div>
  );
}

// PathChips — sensitive_file_access paths carry a write_only flag. Edit via
// ChipEditor over the path strings; new paths default write_only=false and
// existing flags are preserved by matching on path.
type PathEntry = { path: string; write_only: boolean };
function PathChips({ items, onChange }: { items: PathEntry[]; onChange: (items: PathEntry[]) => void }) {
  return (
    <ChipEditor
      value={items.map((p) => p.path)}
      placeholder="path or glob, press Enter"
      onChange={(paths) =>
        onChange(paths.map((path) => items.find((p) => p.path === path) ?? { path, write_only: false }))
      }
    />
  );
}

type NetRow = { addr: string; ports: number[]; proto: ("tcp" | "udp")[] };
function NetworkRows({ rows, editing, onChange }: { rows: NetRow[]; editing?: boolean; onChange: (rows: NetRow[]) => void }) {
  const { token } = theme.useToken();
  if (!editing) {
    if (rows.length === 0) return null;
    return (
      <Table
        size="small"
        pagination={false}
        rowKey={(_, i) => String(i)}
        dataSource={rows}
        columns={[
          { title: "Destination", dataIndex: "addr", render: (v: string) => <code>{v}</code> },
          { title: "Ports", dataIndex: "ports", render: (p: number[]) => <code>{p.join(", ")}</code> },
          { title: "Protocols", dataIndex: "proto", render: (p: string[]) => <code>{p.join(", ")}</code> },
        ]}
      />
    );
  }
  const setRow = (i: number, patch: Partial<NetRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const cycleProto = (i: number) => {
    const cur = rows[i].proto.join("+");
    const next: ("tcp" | "udp")[] = cur === "tcp" ? ["udp"] : cur === "udp" ? ["tcp", "udp"] : ["tcp"];
    setRow(i, { proto: next });
  };
  return (
    <Flex vertical gap={8}>
      {rows.map((r, i) => (
        <Flex key={i} wrap="wrap" align="center" gap={8}>
          <Input size="small" style={{ width: 220 }} value={r.addr} onChange={(e) => setRow(i, { addr: e.target.value })} placeholder="CIDR or hostname" />
          <Input
            size="small"
            style={{ width: 120 }}
            value={r.ports.join(", ")}
            onChange={(e) =>
              setRow(i, {
                ports: e.target.value.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n)),
              })
            }
            placeholder="443, 53"
          />
          <Button size="small" style={{ fontFamily: token.fontFamilyCode }} onClick={() => cycleProto(i)}>
            {r.proto.join("+") || "tcp"}
          </Button>
          <Button size="small" type="text" aria-label="remove destination" icon={<X size={13} />} onClick={() => onChange(rows.filter((_, j) => j !== i))} />
        </Flex>
      ))}
      <div>
        <Button size="small" icon={<Plus size={13} />} onClick={() => onChange([...rows, { addr: "", ports: [], proto: ["tcp"] }])}>
          Add destination
        </Button>
      </div>
    </Flex>
  );
}

// Known coding-model providers as one-click egress allowlist entries. Anthropic
// publishes a stable CIDR (the one destination brace.network IP-locks cleanly);
// the others are CDN-fronted with rotating IPs, so they are pinned by hostname
// (the addr field resolves hostnames at deploy time). Users can still add any
// manual CIDR/hostname below.
type NetProvider = { key: string; label: string; note?: string; dests: NetRow[] };
const NETWORK_PROVIDERS: NetProvider[] = [
  {
    key: "anthropic",
    label: "Anthropic — Claude",
    dests: [
      { addr: "160.79.104.0/23", ports: [443], proto: ["tcp"] },
      { addr: "2607:6bc0::/48", ports: [443], proto: ["tcp"] },
    ],
  },
  { key: "openai", label: "OpenAI — Codex", note: "hostname (IP rotates)", dests: [{ addr: "api.openai.com", ports: [443], proto: ["tcp"] }] },
  { key: "google", label: "Google — Gemini", note: "hostname (IP rotates)", dests: [{ addr: "generativelanguage.googleapis.com", ports: [443], proto: ["tcp"] }] },
  { key: "cursor", label: "Cursor", note: "hostname (IP rotates)", dests: [{ addr: "api2.cursor.sh", ports: [443], proto: ["tcp"] }] },
];
const NET_PROVIDER_ADDRS = new Set(NETWORK_PROVIDERS.flatMap((p) => p.dests.map((d) => d.addr)));

// NetworkProviderPicker — pick provider egress blocks by name (Anthropic CIDR,
// Codex/Gemini/Cursor by hostname), each showing its numeric range/host, plus a
// custom CIDR/hostname editor. Mirrors the model/tool catalog pickers.
function NetworkProviderPicker({ rows, onChange }: { rows: NetRow[]; onChange: (rows: NetRow[]) => void }) {
  const { token } = theme.useToken();
  const addrs = new Set(rows.map((r) => r.addr));
  const isOn = (p: NetProvider) => p.dests.every((d) => addrs.has(d.addr));
  const toggle = (p: NetProvider) => {
    if (isOn(p)) {
      const rm = new Set(p.dests.map((d) => d.addr));
      onChange(rows.filter((r) => !rm.has(r.addr)));
    } else {
      onChange([...rows, ...p.dests.filter((d) => !addrs.has(d.addr))]);
    }
  };
  const customRows = rows.filter((r) => !NET_PROVIDER_ADDRS.has(r.addr));
  const setCustom = (next: NetRow[]) => onChange([...rows.filter((r) => NET_PROVIDER_ADDRS.has(r.addr)), ...next]);
  return (
    <Flex vertical gap={16}>
      <div>
        <FieldLabel>Model providers</FieldLabel>
        <Flex vertical gap={6}>
          {NETWORK_PROVIDERS.map((p) => {
            const on = isOn(p);
            return (
              <label
                key={p.key}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  cursor: "pointer",
                  border: `1px solid ${on ? token.colorSuccess : token.colorBorder}`,
                  background: on ? token.colorSuccessBg : token.colorBgContainer,
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              >
                <Checkbox checked={on} onChange={() => toggle(p)} style={{ marginTop: 2 }} />
                <span style={{ flex: 1 }}>
                  <span>
                    <Text strong style={{ fontSize: 13 }}>{p.label}</Text>
                    {p.note ? (
                      <Tag style={{ marginInlineStart: 8, fontSize: 10 }}>{p.note}</Tag>
                    ) : null}
                  </span>
                  <div style={{ marginTop: 2 }}>
                    <Text style={{ fontFamily: token.fontFamilyCode, fontSize: 11, color: token.colorTextSecondary }}>
                      {p.dests.map((d) => d.addr).join("  ·  ")}  ·  :{p.dests[0].ports.join(",")}/{p.dests[0].proto.join("+")}
                    </Text>
                  </div>
                </span>
              </label>
            );
          })}
        </Flex>
      </div>
      <div>
        <FieldLabel>Custom destinations</FieldLabel>
        <NetworkRows rows={customRows} editing onChange={setCustom} />
      </div>
    </Flex>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type McpServer = any;
function McpServerTable({
  servers,
  editing,
  onServerChange,
  onRemove,
}: {
  servers: Record<string, McpServer>;
  editing?: boolean;
  onServerChange: (name: string, patch: Record<string, unknown>) => void;
  onRemove: (name: string) => void;
}) {
  const rows = Object.entries(servers).map(([name, srv]) => ({ name, srv }));
  return (
    <Table
      size="small"
      pagination={false}
      rowKey="name"
      dataSource={rows}
      columns={[
        { title: "Server", dataIndex: "name", render: (n: string) => <code>{n}</code> },
        { title: "Transport", render: (_: unknown, r: { srv: McpServer }) => <Text type="secondary" style={{ fontSize: 12 }}>{r.srv.transport ?? "—"}</Text> },
        {
          title: "Allowed tools",
          render: (_: unknown, r: { name: string; srv: McpServer }) =>
            editing ? (
              <ChipEditor value={r.srv.allow_list ?? []} placeholder="tool" onChange={(it) => onServerChange(r.name, { allow_list: it })} />
            ) : r.srv.allow_list?.length ? (
              <TagList items={r.srv.allow_list} />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>all except blocked</Text>
            ),
        },
        {
          title: "Blocked tools",
          render: (_: unknown, r: { name: string; srv: McpServer }) =>
            editing ? (
              <ChipEditor value={r.srv.deny_list ?? []} placeholder="tool or *" onChange={(it) => onServerChange(r.name, { deny_list: it })} />
            ) : r.srv.deny_list?.includes("*") ? (
              <Tag color={TOKENS.danger} style={{ marginInlineEnd: 0 }}>All tools</Tag>
            ) : r.srv.deny_list?.length ? (
              <TagList items={r.srv.deny_list} tone="danger" />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>none</Text>
            ),
        },
        ...(editing
          ? [
              {
                title: "",
                width: 40,
                render: (_: unknown, r: { name: string }) => (
                  <Button size="small" type="text" aria-label={`remove ${r.name}`} icon={<X size={13} />} onClick={() => onRemove(r.name)} />
                ),
              },
            ]
          : []),
      ]}
    />
  );
}

// Section — one enforcement area: name left, state/control right, body below.
function Section({
  title,
  area,
  state,
  offNote,
  modeNote,
  editing,
  control,
  highlight,
  children,
}: {
  title: string;
  area: SensorArea;
  state: FamilyState;
  offNote: string;
  modeNote?: string;
  editing?: boolean;
  control?: React.ReactNode;
  highlight?: boolean;
  children: React.ReactNode;
}) {
  const { token } = theme.useToken();
  const isOff = state === "off";
  const greyed = isOff && !editing;
  return (
    <section
      style={{
        padding: "16px 24px",
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        opacity: greyed ? 0.6 : 1,
        background: highlight ? token.colorPrimaryBg : undefined,
      }}
    >
      <Flex align="center" justify="space-between" gap={12}>
        <Text strong style={{ fontFamily: "var(--font-serif, 'Source Serif 4 Variable', serif)", fontSize: 14 }}>
          {title}
        </Text>
        {editing && control ? control : <ModeTag state={state} />}
      </Flex>
      <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 2, marginBottom: 8, maxWidth: 720 }}>
        {SENSOR_INFO[area].blurb}
      </Text>
      {modeNote && !greyed && (
        <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 12 }}>
          {modeNote}
        </Text>
      )}
      {greyed ? <Text type="secondary">{offNote}</Text> : children}
    </section>
  );
}

// StageHeader — a labeled divider marking one rung of the layering journey.
function StageHeader({ n, title, subtitle }: { n: number; title: string; subtitle: string }) {
  const { token } = theme.useToken();
  return (
    <Flex align="flex-start" gap={12} style={{ background: token.colorFillQuaternary, padding: "12px 24px", borderTop: `1px solid ${token.colorBorderSecondary}` }}>
      <span
        style={{
          marginTop: 2,
          display: "flex",
          height: 20,
          width: 20,
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          background: token.colorPrimary,
          fontSize: 11,
          fontWeight: 600,
          color: "#fff",
        }}
      >
        {n}
      </span>
      <div>
        <Text strong style={{ fontFamily: "var(--font-serif, 'Source Serif 4 Variable', serif)", fontSize: 14 }}>
          {title}
        </Text>
        <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
          {subtitle}
        </Text>
      </div>
    </Flex>
  );
}

// ---- Policies list row ----------------------------------------------------

function PolicyRow({
  policy,
  selected,
  onSelect,
  onEdit,
  onChange,
}: {
  policy: Policy;
  selected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onChange: () => void;
}) {
  const { token } = theme.useToken();
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const activate = async () => {
    setBusy(true);
    setErr(null);
    const res = await api.POST("/policies/{policyId}/activate", {
      params: { path: { policyId: policy.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
    });
    setBusy(false);
    if (res.error) {
      setErr(describeError(res.error, res.response.status));
      return;
    }
    onChange();
  };

  const duplicate = async () => {
    const proposed = window.prompt(`Name the copy of "${policy.name}":`, `${policy.name} (copy)`);
    if (proposed === null) return;
    const newName = proposed.trim();
    setBusy(true);
    setErr(null);
    const res = await api.POST("/policies/{policyId}/copy", {
      params: { path: { policyId: policy.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: newName ? { name: newName } : undefined,
    });
    setBusy(false);
    if (res.error) {
      setErr(describeError(res.error, res.response.status));
      return;
    }
    onChange();
  };

  const remove = async () => {
    if (!confirm(`Delete policy "${policy.name}"?`)) return;
    setBusy(true);
    setErr(null);
    const res = await api.DELETE("/policies/{policyId}", {
      params: { path: { policyId: policy.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
    });
    setBusy(false);
    if (res.error) {
      setErr(describeError(res.error, res.response.status));
      return;
    }
    onChange();
  };

  const isEnforce = enforcementMode(policy.content) === "Enforce";

  return (
    <div
      onClick={onSelect}
      aria-selected={selected}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        borderRadius: 8,
        padding: "12px 8px",
        background: selected ? token.colorPrimaryBg : undefined,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <Flex align="center" gap={12} wrap="wrap">
          <span
            style={{ height: 6, width: 6, flexShrink: 0, borderRadius: "50%", background: selected ? token.colorPrimary : "transparent" }}
            aria-hidden
          />
          <span style={{ fontFamily: token.fontFamilyCode, fontSize: 14 }}>{policy.name}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>v{policy.version}</Text>
          {policy.tier_name && (
            <Tag style={{ fontFamily: token.fontFamilyCode, fontSize: 11 }}>tier: {policy.tier_name}</Tag>
          )}
          {policy.active && <Tag color={TOKENS.governance}>Active</Tag>}
          {isEnforce ? <Tag color={TOKENS.danger}>Enforce</Tag> : <Tag>Observe</Tag>}
        </Flex>
        {policy.description && (
          <Text type="secondary" style={{ display: "block", marginTop: 4, marginInlineStart: 18, fontSize: 12 }} ellipsis>
            {policy.description}
          </Text>
        )}
        {err && (
          <Text type="danger" style={{ display: "block", marginTop: 4, marginInlineStart: 18, fontSize: 12 }}>
            {err}
          </Text>
        )}
      </div>
      <Flex gap={4} onClick={(e) => e.stopPropagation()}>
        <Button size="small" type="text" aria-label="Edit policy" title="Edit policy" icon={<Pencil size={14} />} onClick={onEdit} disabled={busy} />
        <Button size="small" type="text" aria-label="Duplicate" title="Duplicate" icon={<Copy size={14} />} onClick={duplicate} disabled={busy} />
        <Button size="small" type="text" danger aria-label="Delete" title="Delete" icon={<Trash2 size={14} />} onClick={remove} disabled={busy} />
        {!policy.active && !policy.tier_name && (
          <Button size="small" type="primary" disabled={busy} onClick={activate}>
            Activate
          </Button>
        )}
      </Flex>
    </div>
  );
}

// ---- Create dialog ---------------------------------------------------------

function CreatePolicyModal({
  policies,
  onClose,
  onCreated,
}: {
  policies: Policy[];
  onClose: () => void;
  onCreated: (p: Policy) => void;
}) {
  const api = useApi();
  const active = policies.find((p) => p.active);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [source, setSource] = useState<string>(active ? active.id : "blank");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setErr("Name the policy first.");
      return;
    }
    setBusy(true);
    setErr(null);
    const src = policies.find((p) => p.id === source);
    const content = src ? JSON.parse(JSON.stringify(src.content)) : emptyContent;
    const res = await api.POST("/policies", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { name: trimmed, description: description.trim() || undefined, content },
    });
    setBusy(false);
    if (res.error) {
      setErr(describeError(res.error, res.response.status));
      return;
    }
    onCreated(res.data as Policy);
  };

  const startOptions = [
    ...(active ? [{ id: active.id, title: `Clone ${active.name} (active)`, detail: "Recommended — start from what the fleet runs today, then adjust." }] : []),
    ...policies.filter((p) => !p.active).slice(0, 3).map((p) => ({ id: p.id, title: `Clone ${p.name}`, detail: p.description || `v${p.version}` })),
    { id: "blank", title: "Blank", detail: "Every enforcement area starts Off — turn on what you need." },
  ];

  return (
    <Modal
      open
      onCancel={onClose}
      title="Create policy"
      okText={busy ? "Creating…" : "Create & edit"}
      okButtonProps={{ disabled: busy }}
      cancelButtonProps={{ disabled: busy }}
      onOk={create}
    >
      <Flex vertical gap={16} style={{ paddingTop: 8 }}>
        <div>
          <FieldLabel>Name</FieldLabel>
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. platform-team-baseline" />
        </div>
        <div>
          <FieldLabel>Description (optional)</FieldLabel>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this policy is for" />
        </div>
        <div>
          <FieldLabel>Start from</FieldLabel>
          <Radio.Group value={source} onChange={(e) => setSource(e.target.value)} style={{ width: "100%" }}>
            <Flex vertical gap={6}>
              {startOptions.map((o) => (
                <StartOption key={o.id} value={o.id} checked={source === o.id} title={o.title} detail={o.detail} />
              ))}
            </Flex>
          </Radio.Group>
        </div>
        {err && <Text type="danger" style={{ fontSize: 12 }}>{err}</Text>}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Opens in the guided editor — nothing deploys until you activate it.
        </Text>
      </Flex>
    </Modal>
  );
}

function StartOption({ value, checked, title, detail }: { value: string; checked: boolean; title: string; detail: string }) {
  const { token } = theme.useToken();
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        cursor: "pointer",
        borderRadius: 8,
        border: `1px solid ${checked ? token.colorPrimary : token.colorBorder}`,
        background: checked ? token.colorPrimaryBg : undefined,
        padding: "8px 12px",
      }}
    >
      <Radio value={value} style={{ marginTop: 2 }} />
      <span>
        <Text style={{ display: "block", fontSize: 14 }}>{title}</Text>
        <Text type="secondary" style={{ display: "block", fontSize: 12 }}>{detail}</Text>
      </span>
    </label>
  );
}
